import logo from "../assets/logo.png";

export default function MemberCard({ member, qrCode, settings }) {
  /*
   * ============================================
   * MEMBER CARD YEAR
   * ============================================
   *
   * First membership:
   *   adhesion → 2026
   *
   * If renewed:
   *   last_renewal_date → 2027, 2028, etc.
   *
   * The most recent renewal year takes priority.
   */
  const cardDate = member.last_renewal_date || member.adhesion || null;

  const cardYear = cardDate ? new Date(cardDate).getFullYear() : "";

  /*
   * ============================================
   * MEMBER NUMBER
   * ============================================
   *
   * The database normally contains:
   *   UM0068
   *
   * But if somewhere else the application already
   * sends:
   *   2026/UM0068
   *
   * we remove the existing year first so that we
   * never produce:
   *   2026/2026/UM0068
   */
  const rawMemberNumber = member.numero_membre || "";

  const cleanMemberNumber = String(rawMemberNumber).replace(/^\d{4}\//, "");

  const displayMemberNumber = cardYear
    ? `${cardYear}/${cleanMemberNumber}`
    : cleanMemberNumber;

  // Display card dates in French numeric format without timezone shifts.
  const formatDisplayDate = (value) => {
    if (!value) return "";

    const text = String(value).trim();
    const match = text.match(/^(\d{4})-(\d{2})-(\d{2})/);

    if (match) {
      return `${match[3]}/${match[2]}/${match[1]}`;
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return text;

    return new Intl.DateTimeFormat("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      timeZone: "Europe/Paris",
    }).format(date);
  };

  return (
    <div
      id="member-card"
      style={{
        width: "85.60mm",
        height: "53.98mm",
        background: "#fff",
        border: "1px solid #999",
        position: "relative",
        overflow: "hidden",
        fontFamily: "Arial, sans-serif",
      }}
    >
      {/* =========================================
          HEADER
      ========================================= */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          height: "16mm",
          padding: "2mm 3mm",
          boxSizing: "border-box",
        }}
      >
        <img
          src={logo}
          alt=""
          style={{
            width: "16mm",
            height: "16mm",
            objectFit: "contain",
            flexShrink: 0,
          }}
        />

        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center",
            height: "100%",
          }}
        >
          <div
            style={{
              fontWeight: "900",
              fontSize: "15px",
              textTransform: "uppercase",
              letterSpacing: "1px",
              lineHeight: 1,
            }}
          >
            {settings?.organization_type}
          </div>

          <div
            style={{
              fontWeight: "900",
              fontSize: "15px",
              textTransform: "uppercase",
              letterSpacing: "1px",
              lineHeight: 1,
            }}
          >
            {settings?.organization_name}
          </div>

          <div
            style={{
              fontSize: "12px",
              marginTop: "2px",
              lineHeight: 1.2,
            }}
          >
            {settings?.association_address}
          </div>
        </div>
      </div>

      {/* =========================================
          ORANGE TITLE BAR
      ========================================= */}
      <div
        style={{
          background: "#f97316",
          color: "#fff",
          textAlign: "center",
          fontWeight: "900",
          fontSize: "14px",
          padding: "4px 0",
        }}
      >
        CARTE D'ADHERENT
      </div>

      {/* =========================================
          MAIN CONTENT
      ========================================= */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          padding: "6px",
          height: "20mm",
          boxSizing: "border-box",
        }}
      >
        {/* LEFT INFO */}
        <div
          style={{
            flex: 1,
            fontSize: "10px",
            lineHeight: 1.25,
          }}
        >
          <div style={{ marginBottom: 3 }}>
            <strong style={{ fontSize: "11px" }}>NOM :</strong> {member.nom}
          </div>

          <div style={{ marginBottom: 3 }}>
            <strong style={{ fontSize: "11px" }}>PRÉNOM :</strong>{" "}
            {member.prenom}
          </div>

          <div style={{ marginBottom: 3 }}>
            <strong style={{ fontSize: "11px" }}>STATUT :</strong> Membre
          </div>

          {/* DATES */}
          <div
            style={{
              marginTop: 5,
              fontSize: "8px",
              color: "#d97706",
              lineHeight: 1.5,
            }}
          >
            <div>
              <strong>Fait le :</strong>{" "}
              {formatDisplayDate(member.card_adhesion || member.adhesion)}
            </div>

            <div>
              <strong>Fin de validité :</strong>{" "}
              {formatDisplayDate(
                member.card_expiration || member.dateExpiration,
              )}
            </div>
          </div>
        </div>

        {/* =========================================
            MEMBER PHOTO
            =========================================
            Added:
              crossOrigin="anonymous"
              encodeURIComponent(member.photo)

            This helps html2canvas load the photo
            reliably during PDF generation.
        ========================================= */}
        {member.photo && (
          <img
            crossOrigin="anonymous"
            src={`/api/image.php?file=${encodeURIComponent(member.photo)}`}
            alt=""
            style={{
              width: 70,
              height: 85,
              objectFit: "cover",
              border: "1px solid #ddd",
            }}
          />
        )}
      </div>

      {/* =========================================
          FOOTER
      ========================================= */}
      <div
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: 20,
          background: "#f97316",
          color: "#fff",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "3px 8px",
          boxSizing: "border-box",
        }}
      >
        {/* CONTACT INFO */}
        <div
          style={{
            fontSize: "9px",
            lineHeight: 1.2,
          }}
        >
          <div>{settings?.association_email}</div>

          <div>
            Tel : {settings?.association_phone}
            {" / "}
            {settings?.association_phone_2}
            {" / "}
            Réf : {settings?.association_reference}
          </div>
        </div>

        {/* MEMBER NUMBER */}
        <div
          style={{
            textAlign: "right",
          }}
        >
          <div
            style={{
              fontSize: "6px",
            }}
          >
            No. D'ADHESION:
          </div>

          <div
            style={{
              fontWeight: "bold",
              fontSize: "10px",
            }}
          >
            {displayMemberNumber}
          </div>
        </div>
      </div>
    </div>
  );
}
